import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export default function Pricing() {
  const { user } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phone, setPhone] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchSubscriptionStatus();
    }
  }, [user]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await api.get('/subscription/status');
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      price: 9.99,
      period: 'month',
      features: ['Income & Expense Tracking', 'Budget Planner', 'Basic Reports', 'Email Support']
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 19.99,
      period: 'month',
      features: ['Everything in Basic', 'Savings Goals', 'Subscription Tracker', 'Net Worth Analysis', 'Priority Support', 'Advanced Reports'],
      popular: true
    },
    {
      id: 'business',
      name: 'Business',
      price: 49.99,
      period: 'month',
      features: ['Everything in Premium', 'Multi-User Access', 'Business Analytics', 'API Access', 'Dedicated Support', 'Custom Reports']
    }
  ];

  const handleSubscribe = (plan) => {
    if (!user) {
      localStorage.setItem('selectedPlan', JSON.stringify({ plan: plan.id, timestamp: Date.now() }));
      toast.info('Please sign in or create an account to subscribe');
      navigate('/login?redirect=pricing');
      return;
    }
    setSelectedPlan(plan);
    setShowPhoneModal(true);
    setPhone(user.phone || '');
  };

  const handleActivateSubscription = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid phone number (e.g., 0712345678)');
      return;
    }

    setProcessing(true);
    try {
      console.log('Activating subscription for plan:', selectedPlan.id);
      console.log('User token exists:', !!localStorage.getItem('token'));
      
      const response = await api.post('/subscribe', {
        plan: selectedPlan.id,
        phone: phone
      });

      console.log('Subscription response:', response.data);

      if (response.data && response.data.success) {
        toast.success('Subscription activated successfully!');
        setShowPhoneModal(false);
        
        // Wait a moment for backend to update
        setTimeout(() => {
          // Redirect to dashboard
          window.location.href = '/dashboard';
        }, 1500);
      } else {
        toast.error(response.data?.error || 'Failed to activate subscription');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to activate subscription. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (user && subscriptionStatus?.subscriptionStatus === 'active') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg inline-block">
            <p className="font-semibold">✅ Your subscription is active!</p>
            <p className="text-sm mt-1">Plan: {subscriptionStatus.subscriptionPlan}</p>
            <button 
              onClick={() => window.location.href = '/dashboard'}
              className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
            Simple, Transparent Pricing
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            Choose the plan that works best for you
          </p>
          <p className="mt-2 text-sm text-indigo-600">
            Subscribe now and get instant access to your dashboard
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={'relative rounded-2xl shadow-lg overflow-hidden ' + (plan.popular ? 'ring-2 ring-indigo-600 scale-105' : 'ring-1 ring-gray-200')}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-indigo-600 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                  Popular
                </div>
              )}
              <div className="bg-white px-6 py-8">
                <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                <p className="mt-4">
                  <span className="text-4xl font-extrabold text-gray-900"></span>
                  <span className="text-base font-medium text-gray-500">/{plan.period}</span>
                </p>
                <ul className="mt-6 space-y-4">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="ml-3 text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={subscriptionStatus?.subscriptionStatus === 'active'}
                  className={'mt-8 w-full py-3 px-4 rounded-lg font-semibold transition ' + (plan.popular ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200')}
                >
                  {subscriptionStatus?.subscriptionStatus === 'active' ? 'Current Plan' : 'Subscribe Now'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Phone Number Modal */}
        {showPhoneModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Activate {selectedPlan?.name} Plan</h2>
                <button onClick={() => setShowPhoneModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Plan: {selectedPlan?.name}</p>
                <p className="text-2xl font-bold text-indigo-600">/{selectedPlan?.period}</p>
              </div>

              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-800 font-semibold">Temporary Activation</p>
                <p className="text-xs text-blue-600">Your subscription will be activated immediately.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0712345678"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Enter your phone number for M-Pesa payment (coming soon)
                </p>
              </div>

              <div className="mt-6 flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowPhoneModal(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleActivateSubscription}
                  disabled={processing}
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {processing ? 'Activating...' : 'Activate Subscription'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

